import { FC, MouseEvent as ReactMouseEvent, useState } from 'react';
import dayjs from 'dayjs';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ICoachSessionListItem } from '../../endpoints/coachSessions';
import { OBJECTIVE_COLORS } from './useCoachSessionWeekView';
import { SessionContextMenu } from './SessionContextMenu';
import styles from './CoachSessionWeekView.module.scss';

interface MonthCalendarProps {
  days: dayjs.Dayjs[];
  sessionsByDate: Record<string, ICoachSessionListItem[]>;
  monthStart: dayjs.Dayjs;
  today: dayjs.Dayjs;
  onDayClick: (date: string) => void;
  onDuplicate: (session: ICoachSessionListItem) => void;
  onMove: (session: ICoachSessionListItem) => void;
  onDelete: (session: ICoachSessionListItem) => void;
  onExportPdf: (session: ICoachSessionListItem) => void;
}

const WEEK_HEADERS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const MonthDot: FC<{
  session: ICoachSessionListItem;
  onContextMenu: (ev: ReactMouseEvent, s: ICoachSessionListItem) => void;
}> = ({ session, onContextMenu }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: session._id,
      disabled: session.status === 'executed',
    });

  const style: React.CSSProperties = {
    background:
      OBJECTIVE_COLORS[session.objective ?? 'mixed'] ?? '#b5b5b5',
    opacity: isDragging ? 0.35 : 1,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    cursor: session.status === 'executed' ? 'default' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      className={styles.monthDot}
      style={style}
      title={session.title}
      onContextMenu={(e) => onContextMenu(e, session)}
      {...listeners}
      {...attributes}
    />
  );
};

const MonthCell: FC<{
  day: dayjs.Dayjs;
  daySessions: ICoachSessionListItem[];
  isCurrentMonth: boolean;
  isToday: boolean;
  onDayClick: (date: string) => void;
  onContextMenu: (ev: ReactMouseEvent, s: ICoachSessionListItem) => void;
}> = ({
  day,
  daySessions,
  isCurrentMonth,
  isToday,
  onDayClick,
  onContextMenu,
}) => {
  const key = day.format('YYYY-MM-DD');
  const { setNodeRef, isOver } = useDroppable({ id: key });

  return (
    <div
      ref={setNodeRef}
      className={[
        styles.monthCell,
        !isCurrentMonth ? styles.otherMonth : '',
        isToday ? styles.today : '',
        isOver ? styles.dayColumnOver : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onDayClick(key)}
    >
      <div className={styles.monthCellNum}>{day.format('D')}</div>
      <div className={styles.monthDots}>
        {daySessions.slice(0, 5).map((s) => (
          <MonthDot key={s._id} session={s} onContextMenu={onContextMenu} />
        ))}
      </div>
    </div>
  );
};

export const MonthCalendar: FC<MonthCalendarProps> = ({
  days,
  sessionsByDate,
  monthStart,
  today,
  onDayClick,
  onDuplicate,
  onMove,
  onDelete,
  onExportPdf,
}) => {
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    session: ICoachSessionListItem;
  } | null>(null);

  const onContextMenu = (
    ev: ReactMouseEvent,
    session: ICoachSessionListItem,
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    setMenu({ x: ev.clientX, y: ev.clientY, session });
  };

  return (
    <div>
      <div className={styles.monthGrid}>
        {WEEK_HEADERS.map((h) => (
          <div key={h} className={styles.monthDayHeader}>
            {h}
          </div>
        ))}
        {days.map((day) => {
          const key = day.format('YYYY-MM-DD');
          const daySessions = sessionsByDate[key] ?? [];
          const isCurrentMonth = day.month() === monthStart.month();
          const isToday = day.isSame(today, 'day');
          return (
            <MonthCell
              key={key}
              day={day}
              daySessions={daySessions}
              isCurrentMonth={isCurrentMonth}
              isToday={isToday}
              onDayClick={onDayClick}
              onContextMenu={onContextMenu}
            />
          );
        })}
      </div>

      {menu && (
        <SessionContextMenu
          x={menu.x}
          y={menu.y}
          onDuplicate={() => onDuplicate(menu.session)}
          onMove={() => onMove(menu.session)}
          onDelete={() => onDelete(menu.session)}
          onExportPdf={() => onExportPdf(menu.session)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
};
